import type { SendEmailInput } from '@prepzio/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { adminApi } from '../lib/api';
import { adminKeys } from '../lib/query';

/**
 * Server-state hooks for the transactional-email screen. History is paginated by
 * cursor, so it uses `useInfiniteQuery`; sending invalidates the history list.
 */

export function useEmailTemplates() {
  return useQuery({
    queryKey: adminKeys.emailTemplates(),
    // Templates need a Full-Access key; treat absence as an empty list, not an error.
    queryFn: () => adminApi.listEmailTemplates().catch(() => ({ items: [] })),
  });
}

export function useEmailTemplateDetail(templateId: string, enabled: boolean) {
  return useQuery({
    queryKey: adminKeys.emailTemplate(templateId),
    queryFn: () => adminApi.getEmailTemplate(templateId),
    enabled: enabled && templateId.length > 0,
  });
}

export function useEmailSends(filters: { status?: string; to?: string }) {
  return useInfiniteQuery({
    queryKey: adminKeys.emailSends(filters),
    queryFn: ({ pageParam }) =>
      adminApi.listEmailSends({
        status: filters.status || undefined,
        to: filters.to?.trim() || undefined,
        limit: 25,
        cursor: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export function useSendEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { input: SendEmailInput; idempotencyKey: string }) =>
      adminApi.sendEmail(vars.input, vars.idempotencyKey),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'email-sends'] }),
  });
}
